import { useEffect, useState } from 'react';
import styled from 'styled-components';

import { useQuery } from '@tanstack/react-query';

import CircularProgress from 'components/shared/CircularProgress';
import Foldout from 'components/shared/Foldout';
import { useProfile } from 'context/profile';
import { simpleDate } from 'lib/date';
import { LocationEpisodeLink } from 'models/flexible/LocationEpisodeLink';
import { LocationEpisodeLinkSet } from 'models/flexible/LocationEpisodeLinkSet';
import { PERMISSIONS } from 'models/flexible/Profile';
import { adminLinkedRecordsQueryKeys, adminShowLinkedRecord } from 'services/api/admin/linkedRecords';
import type { AdminLinkSetShowFields } from 'services/api/fields';
import { colors } from 'styles/theme/colors';

import { InfoBanner, RecordCard, Scenario, SCENARIOS, SlideoutFooter } from './linkDetailsSlideout/index';

type Props = {
  record: LocationEpisodeLinkSet<AdminLinkSetShowFields<'locationEpisodeLinks'>> | null;
  onClose: () => void;
  onUnlink: (record: LocationEpisodeLinkSet<AdminLinkSetShowFields<'locationEpisodeLinks'>>) => void;
  onRelink: (record: LocationEpisodeLinkSet<AdminLinkSetShowFields<'locationEpisodeLinks'>>) => void;
  onApproveLink: (linkId: string) => void;
  onRejectLink: (linkId: string) => void;
  onRemoveLink?: (link: LocationEpisodeLink) => void;
  onApplyChanges?: (approveIds: string[], removeIds: string[]) => void;
};

const getScenario = (record: LocationEpisodeLinkSet<AdminLinkSetShowFields<'locationEpisodeLinks'>>): Scenario => {
  if (record.isUnlinked) return SCENARIOS.UNLINKED;

  const activeCount = record.activeLinks.length;
  const pendingCount = record.pendingLinks.length;

  if (pendingCount === 0) {
    return activeCount === 2 ? SCENARIOS.UNLINK_TWO : SCENARIOS.REMOVE_INDIVIDUAL;
  }

  return activeCount + pendingCount === 2 ? SCENARIOS.APPROVE_TWO : SCENARIOS.APPROVE_MANY;
};

const getLabel = (index: number) => String.fromCharCode(65 + index);

export default function LinkDetailsSlideout({
  record,
  onClose,
  onUnlink,
  onRelink,
  onApproveLink,
  onRejectLink,
  onRemoveLink,
  onApplyChanges,
}: Props) {
  const { profile } = useProfile();
  const canEdit = profile.has(PERMISSIONS.adminLinkedRecordEdit);

  const { data: detailedRecord, isLoading } = useQuery({
    queryKey: adminLinkedRecordsQueryKeys.show(record?.id ?? ''),
    queryFn: ({ signal }) =>
      adminShowLinkedRecord(record!.id, { include: 'locationEpisodeLinks.locationEpisode' }, signal) as Promise<
        LocationEpisodeLinkSet<AdminLinkSetShowFields<'locationEpisodeLinks'>>
      >,
    enabled: !!record?.id,
  });

  const detail = detailedRecord ?? null;
  const scenario = detail ? getScenario(detail) : null;
  const links = detail?.locationEpisodeLinks || [];

  const [removedLinkIds, setRemovedLinkIds] = useState<Set<string>>(new Set());
  const [isApproveEditMode, setIsApproveEditMode] = useState(false);

  useEffect(() => {
    setRemovedLinkIds(new Set());
    setIsApproveEditMode(false);
  }, [record?.id]);

  const handleUnlink = () => {
    if (detail) onUnlink(detail);
  };

  const handleRelink = () => {
    if (detail) onRelink(detail);
  };

  const handleApprove = () => {
    if (scenario === SCENARIOS.APPROVE_MANY) {
      setIsApproveEditMode(true);
    } else {
      const pendingLinks = detail?.pendingLinks || [];
      pendingLinks.forEach((link) => onApproveLink(link.id));
    }
  };

  const handleDeny = () => {
    const pendingLinks = detail?.pendingLinks || [];
    pendingLinks.forEach((link) => onRejectLink(link.id));
  };

  const handleRemove = (link: LocationEpisodeLink) => {
    if (onRemoveLink) onRemoveLink(link);
  };

  const handleMarkForRemoval = (linkId: string) => {
    setRemovedLinkIds((prev) => {
      const next = new Set(prev);
      if (next.has(linkId)) {
        next.delete(linkId);
      } else {
        next.add(linkId);
      }
      return next;
    });
  };

  const handleApply = () => {
    if (!detail || !onApplyChanges) return;

    const allLinks = [...detail.activeLinks, ...detail.pendingLinks];
    const approveIds = allLinks.filter((link) => link.isPending && !removedLinkIds.has(link.id)).map((link) => link.id);
    const removeIds = Array.from(removedLinkIds);

    onApplyChanges(approveIds, removeIds);
    setRemovedLinkIds(new Set());
  };

  const handleCancel = () => {
    setIsApproveEditMode(false);
    setRemovedLinkIds(new Set());
  };

  const showNewMatchBanner = detail?.hasNewMatchForExistingLink ?? false;
  const showUnlinkedBanner = scenario === SCENARIOS.UNLINKED;
  const hasLocalChanges =
    removedLinkIds.size > 0 || (scenario === SCENARIOS.APPROVE_MANY && (detail?.pendingLinks.length ?? 0) > 0);

  const loading = isLoading || !detail || !scenario;

  return (
    <Foldout open={!!record} onBackdropClick={onClose} width='min(80%, 480px)'>
      {record && (
        <PanelContainer>
          <PanelHeader>
            <PanelTitle>Shared Details</PanelTitle>
            <CloseButton onClick={onClose}>&times;</CloseButton>
          </PanelHeader>

          {loading ? (
            <LoadingContainer>
              <CircularProgress />
            </LoadingContainer>
          ) : (
            <>
              <SubHeader>Linked On: {simpleDate(detail.updatedAt)}</SubHeader>
              <PanelContent>
                {showNewMatchBanner && <InfoBanner variant='newMatch' />}
                {showUnlinkedBanner && <InfoBanner variant='unlinked' />}

                <CardsContainer>
                  {links.map((link: LocationEpisodeLink, index: number) => {
                    const isMarkedForRemoval = removedLinkIds.has(link.id);
                    const showRemoveForApproveMany =
                      canEdit && scenario === SCENARIOS.APPROVE_MANY && isApproveEditMode && !link.archived;
                    const showRemoveForIndividual =
                      canEdit && scenario === SCENARIOS.REMOVE_INDIVIDUAL && !link.archived;

                    return (
                      <RecordCard
                        key={link.id}
                        link={link}
                        label={getLabel(index)}
                        showRemove={showRemoveForIndividual || showRemoveForApproveMany}
                        showPending={links.length > 2 && link.isPending && !isMarkedForRemoval && !isApproveEditMode}
                        isArchived={link.archived || isMarkedForRemoval}
                        onRemove={
                          showRemoveForApproveMany ? () => handleMarkForRemoval(link.id) : () => handleRemove(link)
                        }
                      />
                    );
                  })}
                </CardsContainer>
              </PanelContent>

              {canEdit && (
                <SlideoutFooter
                  scenario={scenario}
                  isApproveEditMode={isApproveEditMode}
                  onUnlink={handleUnlink}
                  onApprove={handleApprove}
                  onDeny={handleDeny}
                  onRelink={handleRelink}
                  onApply={handleApply}
                  onCancel={handleCancel}
                  hasChanges={!!hasLocalChanges}
                />
              )}
            </>
          )}
        </PanelContainer>
      )}
    </Foldout>
  );
}

const PanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: white;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid ${colors.black10};
`;

const PanelTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: ${colors.black50};
  line-height: 1;
`;

const SubHeader = styled.div`
  padding: 12px 24px;
  font-size: 12px;
  color: ${colors.black50};
`;

const PanelContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 24px 24px;
`;

const CardsContainer = styled.div`
  display: flex;
  flex-direction: column;

  > *:not(:first-child) {
    border-top: 1px solid ${colors.black10};
    padding-top: 24px;
    margin-top: 24px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 400px;
`;
